// src/store/app.store.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ThemeMode } from "../styles/themes";
import { User } from "../services/firebase-auth.service";

export interface PendingAuthRedirect {
  returnTo: string;
  step?: number;
  reason?: "finalize_surprise" | "share" | "payment" | "generic";
}

interface AppState {
  theme: ThemeMode;
  user: User | null;
  isLoading: boolean;
  notifications: any[];
  pendingAuthRedirect: PendingAuthRedirect | null;

  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
  setUser: (user: User | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  addNotification: (notification: any) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  setPendingAuthRedirect: (payload: PendingAuthRedirect | null) => void;
  clearPendingAuthRedirect: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      theme: "dark",
      user: null,
      isLoading: false,
      notifications: [],
      pendingAuthRedirect: null,

      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === "light" ? "dark" : "light",
        })),

      setTheme: (theme) => set({ theme }),
      setUser: (user) => set({ user }),
      setIsLoading: (isLoading) => set({ isLoading }),

      addNotification: (notification) =>
        set((state) => ({
          notifications: [
            ...state.notifications,
            {
              id: crypto.randomUUID(),
              ...notification,
            },
          ].slice(-50),
        })),

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      clearNotifications: () => set({ notifications: [] }),

      setPendingAuthRedirect: (payload) => set({ pendingAuthRedirect: payload }),
      clearPendingAuthRedirect: () => set({ pendingAuthRedirect: null }),
    }),
    {
      name: "lrnsy-storage",
      partialize: (state) => ({
        theme: state.theme,
        user: state.user,
        pendingAuthRedirect: state.pendingAuthRedirect,
      }),
      version: 2,
    }
  )
);

export const useTheme = () => useAppStore((state) => state.theme);
export const useUser = () => useAppStore((state) => state.user);
export const useNotifications = () => useAppStore((state) => state.notifications);
export const usePendingAuthRedirect = () => useAppStore((state) => state.pendingAuthRedirect);
